import { Form, Input, Button, Upload, Row, Col, Collapse, Select, message } from "antd"
import { EyeOutlined, DeleteOutlined, UserOutlined, FileTextOutlined, CloudUploadOutlined, PaperClipOutlined } from "@ant-design/icons"
import React, { useEffect } from "react"
import { ISapImportValidationError, ISapMasterColumn, ISapPayloadItem } from "../../../types"
import { useUserStore } from "../../../stores/user/userStore"
import ProductPreviewCard from "./ProductPreviewCard"

const TYPE_OF_REQUEST_DESCRIPTIONS: Record<string, string> = {
  Create: "Request to create new master data",
  Extend: "Request to open for another branch",
  Change: "Request to update existing master data",
  Deactivate: "Request to deactivate master data for all units",
}

const NOTE_PLACEHOLDERS: Record<string, string> = {
  Create: "Example: New product needed for mining chemical procurement",
  Extend: "Example: Open existing product for a new branch or plant",
  Change: "Example: Update product description, UOM, or other master data fields",
  Deactivate: "Example: Product is no longer used and should be deactivated",
}

const PRODUCT_USAGE_GUIDE_URL = "https://bumiresourcesmineralstbk.sharepoint.com/:b:/s/brms-sap/IQCTSgj_BgU2QZPlXUE8nABOAepYgQthY0udMmm9tahh3H0?e=pcxnoZ"

interface SapMasterProductFormProps {
  isEditMode: boolean
  form: any
  file: File | null
  loading: boolean
  canSubmit: boolean
  onFileChange: (info: any) => void
  onFinish: (values: any) => void
  previewModalOpen: boolean
  previewLoading: boolean
  previewError: string | null
  previewColumns: ISapMasterColumn[]
  previewItems: ISapPayloadItem[]
  previewIsValid: boolean
  previewRequiredFields: string[]
  previewValidationErrors: ISapImportValidationError[]
  setPreviewModalOpen: (open: boolean) => void
}

const SapMasterProductForm: React.FC<SapMasterProductFormProps> = ({
  isEditMode,
  form,
  file,
  loading,
  canSubmit,
  onFileChange,
  onFinish,
  previewModalOpen,
  previewLoading,
  previewError,
  previewColumns,
  previewItems,
  previewIsValid,
  previewRequiredFields,
  previewValidationErrors,
  setPreviewModalOpen,
}) => {
  const { userData } = useUserStore()
  const selectedTypeOfRequest = Form.useWatch("type_of_request", form)
  const notePlaceholder = NOTE_PLACEHOLDERS[selectedTypeOfRequest || ""] || "Example: Explain the business reason or important context for this request"

  useEffect(() => {
    if (!isEditMode && userData) {
      form.setFieldsValue({
        employee_id: userData.employee_id,
        fullname: userData.fullname,
        level: userData.job_level,
        department: userData.organization_name,
      })
    }
  }, [isEditMode, userData, form])

  const handleRemoveFile = () => {
    setPreviewModalOpen(false)
    onFileChange({ file: { status: "removed" } })
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 1000, margin: "0 auto" }}>
        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <UserOutlined /> User Information
              </>
            }
            key="1"
          >
            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Employee ID" name="employee_id">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Fullname" name="fullname">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Level" name="level">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Department" name="department">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <FileTextOutlined /> Request Details
              </>
            }
            key="1"
          >
            <div style={{ marginBottom: 16, padding: "12px 16px", background: "#faf7f0", border: "1px solid #f0d8a8", borderRadius: 8, color: "#8c6b1f" }}>
              Provide a clear request type and supporting note so approvers can review this request faster.
            </div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Type of Request"
                  name="type_of_request"
                  rules={[{ required: true, message: "Please select type of request" }]}
                  extra={TYPE_OF_REQUEST_DESCRIPTIONS[selectedTypeOfRequest || ""] || "Select the request type that best matches the SAP master data action."}
                >
                  <Select placeholder="Select type of request" disabled={loading} size="large" allowClear>
                    <Select.Option value="Create">Create</Select.Option>
                    <Select.Option value="Extend">Open Unit</Select.Option>
                    <Select.Option value="Change">Change</Select.Option>
                    <Select.Option value="Deactivate">Deactivate</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="Note" name="note" extra="Optional. Use this field to explain the business reason or any important context for approvers.">
                  <Input.TextArea placeholder={notePlaceholder} disabled={loading} rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        {!isEditMode && (
          <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
            <Collapse.Panel
              header={
                <>
                  <FileTextOutlined /> Cara Penggunaan
                </>
              }
              key="1"
            >
              <div style={{ marginBottom: 12, padding: "12px 16px", background: "#faf7f0", border: "1px solid #f0d8a8", borderRadius: 8, color: "#8c6b1f" }}>
                Buka panduan penggunaan Product terlebih dahulu sebelum upload template agar pengisian data sesuai format yang diminta.
              </div>
              <Button type="link" href={PRODUCT_USAGE_GUIDE_URL} target="_blank" rel="noopener noreferrer" style={{ padding: 0 }}>
                Open Product Usage Guide
              </Button>
            </Collapse.Panel>
          </Collapse>
        )}

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <CloudUploadOutlined /> Upload Document
              </>
            }
            key="1"
          >
            <Form.Item name="file" rules={[{ required: true, message: "Please upload a file" }]}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {!file ? (
                  <Upload.Dragger
                    beforeUpload={file => {
                      const isExcel = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || file.type === "application/vnd.ms-excel" || file.name.endsWith(".xls") || file.name.endsWith(".xlsx")
                      if (!isExcel) {
                        message.error("You can only upload Excel file!")
                        return Upload.LIST_IGNORE
                      }
                      return false
                    }}
                    accept=".xls,.xlsx"
                    onChange={onFileChange}
                    fileList={[]}
                    maxCount={1}
                    showUploadList={false}
                    disabled={loading}
                    style={{ padding: 32, background: "#fafafa", border: "1px dashed #d9d9d9", borderRadius: 8 }}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined style={{ color: "#1890ff", fontSize: 48 }} />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">Support for a single upload. Only .xls and .xlsx files are allowed.</p>
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
                      <a href="https://bumiresourcesmineralstbk.sharepoint.com/:x:/s/brms-sap/IQCsN2wWJQXkSZof4ujHoM4BAaBb7apakXr9i5HA0KQJv-E?e=LYyX0Z" target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                        Download Template
                      </a>
                    </div>
                  </Upload.Dragger>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 16,
                      padding: 16,
                      background: "#f0faff",
                      border: "1px solid #bae7ff",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, overflow: "hidden", flex: 1, minWidth: 200 }}>
                      <div
                        style={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          background: "#1890ff",
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PaperClipOutlined style={{ fontSize: 20, color: "#fff" }} />
                      </div>
                      <div style={{ overflow: "hidden", flex: 1 }}>
                        <div style={{ fontWeight: 500, color: "#262626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{(file.size / 1024).toFixed(2)} KB</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button size="small" type="default" icon={previewModalOpen ? <EyeOutlined /> : <EyeOutlined />} onClick={() => setPreviewModalOpen(!previewModalOpen)} style={{ display: "flex", alignItems: "center" }}>
                        {previewModalOpen ? "Hide" : "Preview"}
                      </Button>
                      <Button size="small" danger type="default" icon={<DeleteOutlined />} onClick={handleRemoveFile} style={{ display: "flex", alignItems: "center" }}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Form.Item>
          </Collapse.Panel>
        </Collapse>

        {previewModalOpen && (
          <div style={{ marginBottom: 24 }}>
            <ProductPreviewCard columns={previewColumns} items={previewItems} loading={previewLoading} error={previewError} requiredFields={previewRequiredFields} validationErrors={previewIsValid ? [] : previewValidationErrors} />
          </div>
        )}

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button onClick={() => window.history.back()}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading} disabled={!canSubmit} style={{ minWidth: 120 }}>
              {isEditMode ? "Update Product" : "Create Product"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </>
  )
}

export default SapMasterProductForm
